import React, { useState, useEffect } from 'react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState, useAppDispatch } from '../../store';
import apis from '../../utils/apis';
import BaseForm, { BaseFormSectionConfig } from '../shared/FormComponents/BaseForm';
import CheckBox from '../shared/TailwindComponents/CheckBox';
import Skeleton from '../shared/TailwindComponents/Skeleton';
import { FIELD_LABEL_CLASS, FOCUS_RING } from '../shared/TailwindComponents/FieldShell';
import { FormSchema, FormField } from '../../utils/validation/form-schema';
import { PiGearLight, PiCaretDownLight } from 'react-icons/pi';
import * as PiIcons from 'react-icons/pi';
import { showFeedbackModal } from '../../store/actions/UserFeedbackActions';
import { readErrorMessage } from '../../utils/errorMessage';

interface Permission {
  id: number;
  name: string;
  section_id: number;
  description?: string;
}

interface PermissionSection {
  id: number;
  name: string;
  category?: string;
  description?: string;
  permissions: Permission[];
  permission_count: number;
}

const sectionIconMap: Record<string, string> = {
  'Dashboard': 'PiDashboard',
  'Tasks': 'PiCheckSquare',
  'Calendar': 'PiCalendar',
  'Messages': 'PiChat',
  'Clients': 'PiUsers',
  'Lawyers': 'PiUserCircle',
  'Projects': 'PiTreeStructure',
  'Matters': 'PiBriefcase',
  'Files': 'PiFiles',
  'Research': 'PiMagnifyingGlass',
  'Tables': 'PiGridFour',
  'Analytics': 'PiChartBar',
  'Data Sources': 'PiDatabase',
  'Finder': 'PiUserSwitch',
  'Templates': 'PiFileText',
  'People': 'PiUsers',
  'Departments': 'PiBuilding',
  'Teams': 'PiUsersThree',
  'Finance': 'PiCoinVertical',
};

const getIconByName = (name: string): JSX.Element => {
  const normalizedName = name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();
  const spacedName = name.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  const iconKey = sectionIconMap[normalizedName] || sectionIconMap[spacedName] || sectionIconMap[name] || 'PiGear';
  const IconComponent = (PiIcons as any)[iconKey + 'Light'] || (PiIcons as any)[iconKey];
  return IconComponent ? <IconComponent className="h-5 w-5" /> : <PiGearLight className="h-5 w-5" />;
};

// Permission display constants
const HIDDEN_PERMISSION_SECTIONS = ['dashboard', 'research', 'analytics', 'client_pipeline', 'messages', 'finance', 'review', 'notifications'];

const SECTION_DISPLAY_ORDER = [
  'tasks', 'calendar',
  'clients', 'matters', 'files',
  'tables', 'templates', 'data_sources',
  'people', 'departments', 'teams',
];

const CATEGORY_ORDER = ['core', 'workspace', 'utilities', 'organization'];

const CATEGORY_LABELS: Record<string, string> = {
  core: 'Core',
  workspace: 'Workspace',
  utilities: 'Utilities',
  organization: 'Organization',
};

const formatSectionName = (name: string): string => {
  return name.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
};

const formatPermissionLabel = (permission: { name: string; description?: string }): string => {
  if (permission.description) return permission.description;
  const parts = permission.name.split(':');
  const action = parts[1] || parts[0];
  return action.charAt(0).toUpperCase() + action.slice(1);
};

// Custom Permissions Selector Component
interface PermissionsSelectorProps {
  value: number[];
  onChange: (value: number[]) => void;
  permissions: PermissionSection[];
  disabled?: boolean;
  isLoading?: boolean;
}

const PermissionsSelector: React.FC<PermissionsSelectorProps> = ({
  value,
  onChange,
  permissions,
  disabled = false,
  isLoading = false
}) => {
  const { t } = useTranslation();
  const [expandedSections, setExpandedSections] = useState<Record<number, boolean>>({});

  const handleSectionToggle = (sectionId: number) => {
    setExpandedSections((prev) => ({
      ...prev,
      [sectionId]: !prev[sectionId],
    }));
  };

  const handlePermissionToggle = (permissionId: number) => {
    onChange(
      value.includes(permissionId)
        ? value.filter((id) => id !== permissionId)
        : [...value, permissionId]
    );
  };

  if (isLoading) {
    return <Skeleton variant="list" rows={6} />;
  }

  const filteredSections = permissions
    .filter(s => !HIDDEN_PERMISSION_SECTIONS.includes(s.name.toLowerCase()))
    .sort((a, b) => {
      const idxA = SECTION_DISPLAY_ORDER.indexOf(a.name.toLowerCase());
      const idxB = SECTION_DISPLAY_ORDER.indexOf(b.name.toLowerCase());
      return (idxA === -1 ? 999 : idxA) - (idxB === -1 ? 999 : idxB);
    });

  const groupedSections = CATEGORY_ORDER
    .map(cat => ({
      category: cat,
      label: CATEGORY_LABELS[cat],
      sections: filteredSections.filter(s => (s.category || 'core') === cat),
    }))
    .filter(g => g.sections.length > 0);

  return (
    <div className="space-y-6">
      {groupedSections.map((group) => (
        <div key={group.category}>
          <h3 className={clsx(FIELD_LABEL_CLASS, 'mb-3')}>
            {group.label}
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {group.sections.map((section) => (
              <div
                key={section.id}
                className="border border-hairline rounded-md overflow-hidden bg-surface-sunken/70"
              >
                <button
                  type="button"
                  onClick={() => handleSectionToggle(section.id)}
                  className={clsx(
                    'w-full flex items-center justify-between p-3 transition-colors hover:bg-surface-sunken',
                    FOCUS_RING
                  )}
                  aria-expanded={!!expandedSections[section.id]}
                  disabled={disabled}
                >
                  <div className="flex items-center gap-2 flex-1 text-start min-w-0">
                    <div className="text-fg-muted flex-shrink-0">
                      {getIconByName(section.name)}
                    </div>
                    <div className="min-w-0">
                      <h4 className="font-medium text-fg-strong text-sm truncate">
                        {formatSectionName(section.name)}
                      </h4>
                      {section.description && (
                        <p className="text-xs text-fg-muted truncate">
                          {section.description}
                        </p>
                      )}
                    </div>
                  </div>
                  <PiCaretDownLight
                    className={clsx(
                      'w-4 h-4 text-fg-muted transition-transform flex-shrink-0 ms-2',
                      expandedSections[section.id] && 'rotate-180'
                    )}
                  />
                </button>

                {expandedSections[section.id] && (
                  <div className="border-t border-hairline-soft px-3 py-2 space-y-2 bg-surface/60">
                    {section.permissions.length > 0 ? (
                      section.permissions.map((permission) => (
                        <CheckBox
                          key={permission.id}
                          id={`permission-${permission.id}`}
                          label={formatPermissionLabel(permission)}
                          translate={false}
                          checked={value.includes(permission.id)}
                          onChange={() => handlePermissionToggle(permission.id)}
                          disabled={disabled}
                          className="p-1"
                        />
                      ))
                    ) : (
                      <p className="text-xs text-fg-muted">
                        {t('No permissions available')}
                      </p>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

const CreateRoleForm: React.FC = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useAppDispatch();
  const userInfo = useSelector((state: RootState) => state.user.userInfo);

  const [permissions, setPermissions] = useState<PermissionSection[]>([]);
  const [isLoadingPermissions, setIsLoadingPermissions] = useState(true);

  useEffect(() => {
    const fetchPermissions = async () => {
      try {
        setIsLoadingPermissions(true);
        const orgType = (userInfo as any)?.organization_type || (userInfo as any)?.OrganizationType || 'law_firm';
        const res = await apis.get('/admin/permissions', {
          params: {
            org_type: orgType
          }
        });
        if (res && res.data && res.data.data) {
          setPermissions(res.data.data);
        }
      } catch (err) {
        console.error('Error fetching permissions:', err);
        dispatch(showFeedbackModal({
          showModal: true,
          message: t('Failed to load permissions'),
          modalType: 'error',
          duration: 4000
        }));
      } finally {
        setIsLoadingPermissions(false);
      }
    };

    fetchPermissions();
  }, []);

  // Create form schema
  const formSchema = new FormSchema('CreateRoleForm', [
    new FormField('display_name', 'text', true, t('Role Name'), { minLength: 1, maxLength: 255 }),
    new FormField('description', 'text', false, t('Description'), { minLength: 0, maxLength: 500 }),
    new FormField('permission_ids', 'text', false, t('Permissions')),
  ]);

  // Build form sections with permissions
  const permissionsSelectorProps = {
    permissions,
    isLoading: isLoadingPermissions,
  };

  const formSections: BaseFormSectionConfig[] = [
    {
      title: t('Role Details'),
      description: t('Name the role and describe what it is for'),
      gridCols: 2,
      fields: [
        {
          name: 'display_name',
          type: 'input',
          label: t('Role Name'),
          placeholder: t('e.g., Senior Attorney'),
          required: true,
          gridSize: 'half',
        },
        {
          name: 'description',
          type: 'input',
          label: t('Description'),
          placeholder: t('Brief description'),
          gridSize: 'half',
        },
      ],
    },
    {
      title: t('Permissions'),
      description: t('Select which features and actions this role can access'),
      fields: [
        {
          // The section above already says "Permissions" - no field label too.
          name: 'permission_ids',
          type: 'custom',
          label: '',
          required: false,
          customComponent: PermissionsSelector,
          customProps: permissionsSelectorProps,
        },
      ],
    },
  ];

  const handleFormSubmit = async (formData: Record<string, any>) => {
    try {
      const displayName = (formData.display_name || '').trim();
      if (!displayName || displayName.length < 2) {
        throw new Error(t('Role name must be at least 2 characters'));
      }

      const payload = {
        display_name: displayName,
        description: formData.description || '',
        permission_ids: formData.permission_ids || [],
      };

      const res = await apis({
        method: 'POST',
        url: '/admin/roles',
        data: payload,
      });

      if (res && res.data?.success) {
        dispatch(showFeedbackModal({
          showModal: true,
          message: t('Role created successfully'),
          modalType: 'success',
          duration: 3000
        }));
        setTimeout(() => {
          history.push('/admin?tab=roles');
        }, 1500);
      }
    } catch (err: any) {
      console.error('Error creating role:', err);
      throw new Error(readErrorMessage(err, t('Failed to create role')));
    }
  };

  const handleFormCancel = () => {
    history.goBack();
  };

  return (
    <BaseForm
      title={t('Create New Role')}
      description={t('Define a role and assign permissions to it')}
      sections={formSections}
      schema={formSchema}
      initialValues={{
        display_name: '',
        description: '',
        permission_ids: [],
      }}
      onSubmit={handleFormSubmit}
      onCancel={handleFormCancel}
      submitLabel={t('Create Role')}
      cancelLabel={t('Cancel')}
      showConfirmationModal={true}
      maxWidth="max-w-2xl"
    />
  );
};

export default CreateRoleForm;
